import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import ScheduleModal from './ScheduleModal';
import { 
  Calendar, Clock, Monitor, Plus, Edit2, Trash2, Power,
  Copy, ChevronRight, AlertCircle, Check, X
} from 'lucide-react';

const AdvancedScheduler = ({ currentUser }) => {
  const { t } = useTranslation();
  const [schedules, setSchedules] = useState([]);
  const [playlists, setPlaylists] = useState([]);
  const [screens, setScreens] = useState([]);
  const [showScheduleModal, setShowScheduleModal] = useState(false);
  const [editingSchedule, setEditingSchedule] = useState(null);
  const [currentlyActive, setCurrentlyActive] = useState(null);

  const API_URL = (process.env.REACT_APP_API_URL?.replace('/api', '') || 'https://app.alpenscreen.ch');
  const token = localStorage.getItem('token');

  const weekDays = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'];

  // Fetch Data
  useEffect(() => {
    fetchSchedules();
    fetchPlaylists();
    fetchScreens();
  }, []);

  const fetchSchedules = async () => {
    try {
      const res = await fetch(`${API_URL}/api/schedules`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      const data = await res.json();
      setSchedules(data);
      
      // Find currently active schedule
      const now = new Date();
      const currentDay = now.getDay(); // 0 = Sunday, 1 = Monday, etc.
      const currentTime = now.getHours() * 60 + now.getMinutes();
      
      const active = data.find(schedule => {
        if (!schedule.is_active) return false;
        
        const days = schedule.days_of_week || [];
        if (!days.includes(currentDay)) return false;
        
        const [startHour, startMin] = schedule.start_time.split(':').map(Number);
        const [endHour, endMin] = schedule.end_time.split(':').map(Number);
        const startMinutes = startHour * 60 + startMin;
        const endMinutes = endHour * 60 + endMin;
        
        return currentTime >= startMinutes && currentTime <= endMinutes;
      });
      
      setCurrentlyActive(active);
    } catch (err) {
      console.error('Error fetching schedules:', err);
    }
  };

  const fetchPlaylists = async () => {
    try {
      const res = await fetch(`${API_URL}/api/playlists`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      const data = await res.json();
      setPlaylists(data);
    } catch (err) {
      console.error('Error fetching playlists:', err);
    }
  };

  const fetchScreens = async () => {
    try {
      const res = await fetch(`${API_URL}/api/screens`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      const data = await res.json();
      setScreens(data);
    } catch (err) {
      console.error('Error fetching screens:', err);
    }
  };

  // Create Schedule
  const handleCreateSchedule = async (formData) => {
    try {
      const res = await fetch(`${API_URL}/api/schedules`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(formData)
      });

      if (!res.ok) {
        const error = await res.json();
        throw new Error(error.error || t('scheduler.errors.createFailed'));
      }

      setShowScheduleModal(false);
      setEditingSchedule(null);
      fetchSchedules();
    } catch (error) {
      alert(t('common.error') + ': ' + error.message);
    }
  };

  // Update Schedule
  const handleUpdateSchedule = async (id, formData) => {
    try {
      const res = await fetch(`${API_URL}/api/schedules/${id}`, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(formData)
      });

      if (!res.ok) {
        const error = await res.json();
        throw new Error(error.error || t('scheduler.errors.updateFailed'));
      }

      setShowScheduleModal(false);
      setEditingSchedule(null);
      fetchSchedules();
    } catch (error) {
      alert(t('common.error') + ': ' + error.message);
    }
  };

  // Delete Schedule
  const deleteSchedule = async (id) => {
    if (!window.confirm(t('scheduler.deleteConfirm'))) return;

    try {
      await fetch(`${API_URL}/api/schedules/${id}`, {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${token}` }
      });
      fetchSchedules();
    } catch (error) {
      alert(t('scheduler.errors.deleteFailed'));
    }
  };

  // Open Edit Modal
  const openEditModal = (schedule) => {
    setEditingSchedule(schedule);
    setShowScheduleModal(true);
  };

  // Toggle Schedule Active/Inactive
  const toggleSchedule = async (scheduleId, currentStatus) => {
    try {
      const res = await fetch(`${API_URL}/api/schedules/${scheduleId}`, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ is_active: !currentStatus })
      });

      if (res.ok) {
        fetchSchedules();
      }
    } catch (err) {
      console.error('Error toggling schedule:', err);
    }
  };

  // Duplicate Schedule
  const duplicateSchedule = async (schedule) => {
    try {
      const newSchedule = {
        name: `${schedule.name} (${t('scheduler.copy')})`,
        screen_ids: schedule.screen_ids,
        playlist_id: schedule.playlist_id,
        start_time: schedule.start_time,
        end_time: schedule.end_time,
        days_of_week: schedule.days_of_week,
        priority: schedule.priority,
        is_active: false
      };

      const res = await fetch(`${API_URL}/api/schedules`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(newSchedule)
      });

      if (res.ok) {
        fetchSchedules();
      }
    } catch (err) {
      console.error('Error duplicating schedule:', err);
    }
  };

  // Get Priority Badge
  const getPriorityBadge = (priority) => {
    if (priority >= 7) return { label: t('scheduler.priority.high'), color: 'bg-red-100 text-red-700 dark:bg-red-900 dark:text-red-300' };
    if (priority >= 4) return { label: t('scheduler.priority.medium'), color: 'bg-yellow-100 text-yellow-700 dark:bg-yellow-900 dark:text-yellow-300' };
    return { label: t('scheduler.priority.low'), color: 'bg-green-100 text-green-700 dark:bg-green-900 dark:text-green-300' };
  };

  // Get Day Label
  const getDayLabel = (dayNumber) => {
    const days = [
      t('scheduler.days.sun'),
      t('scheduler.days.mon'),
      t('scheduler.days.tue'),
      t('scheduler.days.wed'),
      t('scheduler.days.thu'),
      t('scheduler.days.fri'),
      t('scheduler.days.sat')
    ];
    return days[dayNumber] || '';
  };

  // Stats
  const stats = {
    total: schedules.length,
    active: schedules.filter(s => s.is_active).length,
    screens: [...new Set(schedules.flatMap(s => s.screen_ids || []))].length
  };

  return (
    <div className="space-y-6">
      {/* Currently Active Preview */}
      {currentlyActive && (
        <div className="bg-gradient-to-r from-green-50 to-emerald-50 dark:from-green-900/20 dark:to-emerald-900/20 border-2 border-green-500 dark:border-green-600 rounded-lg p-6">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <div className="w-12 h-12 bg-green-500 rounded-full flex items-center justify-center animate-pulse">
                <Clock className="text-white" size={24} />
              </div>
              <div>
                <h3 className="text-lg font-bold text-green-900 dark:text-green-100">
                  {t('scheduler.currentlyActive')}
                </h3>
                <p className="text-green-700 dark:text-green-300 font-medium">
                  {currentlyActive.name}
                </p>
                <p className="text-sm text-green-600 dark:text-green-400">
                  {currentlyActive.start_time} - {currentlyActive.end_time} • 
                  {t('scheduler.playlist')}: {playlists.find(p => p.id === currentlyActive.playlist_id)?.name || t('scheduler.unknown')}
                </p>
              </div>
            </div>
            <div className="text-right">
              <span className="text-xs text-green-700 dark:text-green-300 font-medium">
                {t('scheduler.screensCount', { count: screens.filter(s => currentlyActive.screen_ids?.includes(s.id)).length })}
              </span>
            </div>
          </div>
        </div>
      )}

      {/* Stats Cards */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-4">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-600 dark:text-gray-400">{t('scheduler.totalRules')}</p>
              <p className="text-2xl font-bold text-gray-900 dark:text-white">{stats.total}</p>
            </div>
            <Calendar className="text-primary-600" size={32} />
          </div>
        </div>

        <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-4">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-600 dark:text-gray-400">{t('scheduler.activeRules')}</p>
              <p className="text-2xl font-bold text-gray-900 dark:text-white">{stats.active}</p>
            </div>
            <Check className="text-green-500" size={32} />
          </div>
        </div>

        <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-4">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-600 dark:text-gray-400">{t('scheduler.screensManaged')}</p>
              <p className="text-2xl font-bold text-gray-900 dark:text-white">{stats.screens}</p>
            </div>
            <Monitor className="text-purple-500" style={{ fontSize: '${32 * fontMultiplier}px' }} />
          </div>
        </div>
      </div>

      {/* Header */}
      <div className="flex justify-between items-center">
        <div>
          <h2 className="text-2xl font-bold text-gray-800 dark:text-gray-100">
            {t('scheduler.scheduleRules')}
          </h2>
          <p className="text-sm text-gray-600 dark:text-gray-400 mt-1">
            {t('scheduler.automatePlaylistChanges')}
          </p>
        </div>
        <button
          onClick={() => setShowScheduleModal(true)}
          className="flex items-center gap-2 bg-primary-600 hover:bg-primary-700 text-white px-4 py-2 rounded-lg transition-colors"
        >
          <Plus size={20} /> {t('scheduler.createRule')}
        </button>
      </div>

      {/* Schedule Rules List */}
      <div className="space-y-4">
        {schedules.length === 0 ? (
          <div className="text-center py-12 bg-white dark:bg-gray-800 rounded-lg">
            <Calendar className="mx-auto text-gray-400 mb-4" size={64} />
            <h3 className="text-xl font-semibold text-gray-700 dark:text-gray-300 mb-2">
              {t('scheduler.noScheduleRules')}
            </h3>
            <p className="text-gray-500 dark:text-gray-400 mb-4">
              {t('scheduler.createFirstRule')}
            </p>
          </div>
        ) : (
          schedules.map(schedule => {
            const priority = getPriorityBadge(schedule.priority);
            const playlist = playlists.find(p => p.id === schedule.playlist_id);
            const assignedScreens = screens.filter(s => schedule.screen_ids?.includes(s.id));

            return (
              <div
                key={schedule.id}
                className={`bg-white dark:bg-gray-800 rounded-lg shadow-md p-6 border-l-4 transition-all ${
                  schedule.is_active 
                    ? 'border-green-500 hover:shadow-lg' 
                    : 'border-gray-300 dark:border-gray-600 opacity-60'
                }`}
              >
                <div className="flex items-start justify-between">
                  {/* Left: Info */}
                  <div className="flex-1">
                    <div className="flex items-center gap-3 mb-3">
                      <h3 className="text-lg font-bold text-gray-900 dark:text-white">
                        {schedule.name}
                      </h3>
                      <span className={`text-xs px-2 py-1 rounded-full font-medium ${priority.color}`}>
                        {t('scheduler.priorityLabel')} {schedule.priority}
                      </span>
                      {schedule.is_active && (
                        <span className="text-xs px-2 py-1 rounded-full bg-green-100 text-green-700 dark:bg-green-900 dark:text-green-300 font-medium flex items-center gap-1">
                          <div className="w-2 h-2 bg-green-500 rounded-full animate-pulse"></div>
                          {t('scheduler.active')}
                        </span>
                      )}
                    </div>

                    {/* Schedule Details */}
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-4 text-sm">
                      {/* Days */}
                      <div>
                        <p className="text-gray-500 dark:text-gray-400 mb-1 flex items-center gap-1">
                          <Calendar size={14} />
                          {t('scheduler.days.label')}
                        </p>
                        <div className="flex gap-1 flex-wrap">
                          {[0, 1, 2, 3, 4, 5, 6].map(dayNum => (
                            <span
                              key={dayNum}
                              className={`text-xs px-2 py-1 rounded ${
                                schedule.days_of_week?.includes(dayNum)
                                  ? 'bg-blue-100 text-blue-700 dark:bg-blue-900 dark:text-blue-300 font-medium'
                                  : 'bg-gray-100 text-gray-400 dark:bg-gray-700 dark:text-gray-500'
                              }`}
                            >
                              {getDayLabel(dayNum)}
                            </span>
                          ))}
                        </div>
                      </div>

                      {/* Time */}
                      <div>
                        <p className="text-gray-500 dark:text-gray-400 mb-1 flex items-center gap-1">
                          <Clock size={14} />
                          {t('scheduler.timeRange')}
                        </p>
                        <p className="text-gray-900 dark:text-white font-medium">
                          {schedule.start_time} - {schedule.end_time}
                        </p>
                      </div>

                      {/* Screens */}
<div>
  <p className="text-gray-500 dark:text-gray-400 mb-1 flex items-center gap-2">
  <Monitor size={14} />
  {t('screens.screens')}
  <span className="inline-flex items-center justify-center w-5 h-5 text-xs font-medium bg-blue-100 dark:bg-blue-900 text-blue-800 dark:text-blue-200 rounded-full">
    {assignedScreens.length}
  </span>
</p>
  <p className="text-gray-900 dark:text-white font-medium truncate">
    {assignedScreens.length > 0 
      ? assignedScreens.map(s => s.name).join(', ')
      : t('scheduler.noScreensAssigned')}
  </p>
</div>
                    </div>

                    {/* Playlist */}
                    <div className="mt-3 pt-3 border-t border-gray-200 dark:border-gray-700">
                      <div className="flex items-center gap-2">
                        <ChevronRight size={16} className="text-gray-400" />
                        <span className="text-sm text-gray-600 dark:text-gray-400">{t('scheduler.playlist')}:</span>
                        <span className="text-sm font-semibold text-gray-900 dark:text-white">
                          {playlist?.name || t('scheduler.unknown')}
                        </span>
                        {playlist && (
                          <span className="text-xs text-gray-500 dark:text-gray-400">
                            ({playlist.media_count || 0} {t('playlists.items')})
                          </span>
                        )}
                      </div>
                    </div>
                  </div>

                  {/* Right: Actions */}
                  <div className="flex flex-col gap-2 ml-4">
                    {/* Toggle Active */}
                    <button
                      onClick={() => toggleSchedule(schedule.id, schedule.is_active)}
                      className={`p-2 rounded-lg transition-colors ${
                        schedule.is_active
                          ? 'bg-green-100 text-green-700 hover:bg-green-200 dark:bg-green-900 dark:text-green-300'
                          : 'bg-gray-100 text-gray-500 hover:bg-gray-200 dark:bg-gray-700 dark:text-gray-400'
                      }`}
                      title={schedule.is_active ? t('scheduler.deactivate') : t('scheduler.activate')}
                    >
                      <Power size={18} />
                    </button>

                    {/* Edit */}
                    <button
                      onClick={() => openEditModal(schedule)}
                      className="p-2 rounded-lg bg-blue-100 text-blue-700 hover:bg-blue-200 dark:bg-blue-900 dark:text-blue-300 transition-colors"
                      title={t('common.edit')}
                    >
                      <Edit2 size={18} />
                    </button>

                    {/* Duplicate */}
                    <button
                      onClick={() => duplicateSchedule(schedule)}
                      className="p-2 rounded-lg bg-purple-100 text-purple-700 hover:bg-purple-200 dark:bg-purple-900 dark:text-purple-300 transition-colors"
                      title={t('scheduler.duplicate')}
                    >
                      <Copy size={18} />
                    </button>

                    {/* Delete */}
                    <button
                      onClick={() => deleteSchedule(schedule.id)}
                      className="p-2 rounded-lg bg-red-100 text-red-700 hover:bg-red-200 dark:bg-red-900 dark:text-red-300 transition-colors"
                      title={t('common.delete')}
                    >
                      <Trash2 size={18} />
                    </button>
                  </div>
                </div>
              </div>
            );
          })
        )}
      </div>

      {/* Info Box */}
      <div className="bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg p-4">
        <div className="flex gap-3">
          <AlertCircle className="text-primary-600 dark:text-primary-400 flex-shrink-0" size={20} />
          <div>
            <h4 className="font-semibold text-blue-900 dark:text-blue-100 mb-1">
              {t('scheduler.howScheduleRulesWork')}
            </h4>
            <p className="text-sm text-blue-800 dark:text-blue-200">
              {t('scheduler.scheduleRulesDescription')}
            </p>
          </div>
        </div>
      </div>

      {/* Schedule Modal */}
      {showScheduleModal && (
        <ScheduleModal
          screens={screens}
          playlists={playlists}
          editingSchedule={editingSchedule}
          onClose={() => {
            setShowScheduleModal(false);
            setEditingSchedule(null);
          }}
          onCreate={handleCreateSchedule}
          onUpdate={handleUpdateSchedule}
        />
      )}
    </div>
  );
};

export default AdvancedScheduler;